import { el, clear } from '../../core/dom.js';
import { withProduct } from '../../core/route-map.js';
import { navigate } from '../../core/router.js';
import { toast } from '../../components/toast.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { armDestructive, resetDestructive } from '../settings/confirm.js';
import {
  archiveProduct, countDocuments, getProduct, restoreProduct, updateProduct,
} from './actions.js';
import {
  connectorRows,
  healthLabel,
  overviewStats,
  platformsList,
  productIsArchived,
  productUpdatePayload,
  readinessLabel,
} from './model.js';

/**
 * Product context / settings panel (plan 4 R11). No artboard - matches v2 tokens.
 * @param {{
 *   productId: string,
 *   me: object,
 *   focusProductId?: string,
 * }} opts
 */
export function renderProductContext(opts) {
  const focusId = opts.focusProductId || opts.productId;
  const isAdmin = !!(opts.me && opts.me.role === 'admin');
  const root = el('div', { class: 'pc' });
  const host = el('div', { class: 'pc-host' });
  root.append(host);

  const ui = {
    dead: false,
    loading: true,
    fatal: '',
    product: null,
    integrations: [],
    documentCount: 0,
    tab: 'overview',
    editing: false,
  };

  root.addEventListener('bonnie:unmount', () => { ui.dead = true; });

  function paint() {
    if (ui.dead) return;
    clear(host);
    if (ui.loading) {
      host.append(skeleton(6));
      return;
    }
    if (ui.fatal || !ui.product) {
      host.append(errorPanel({
        title: 'Product not found',
        message: ui.fatal || 'That product is not available in this workspace.',
        action: button('Back to products', {
          variant: 'primary',
          href: withProduct('/app/settings/products', opts.productId),
        }),
      }));
      return;
    }
    host.append(page(ui, { isAdmin, shellProductId: opts.productId }));
  }

  async function load() {
    ui.loading = true;
    ui.fatal = '';
    paint();
    const detail = await getProduct(focusId);
    if (ui.dead) return;
    if (!detail || detail.error || !detail.product) {
      ui.loading = false;
      ui.fatal = (detail && detail.error) || 'Could not load product';
      ui.product = null;
      paint();
      return;
    }
    ui.product = detail.product;
    ui.integrations = detail.integrations || [];
    const docs = await countDocuments(focusId);
    if (ui.dead) return;
    ui.documentCount = docs && !docs.error && Array.isArray(docs.results)
      ? docs.results.length
      : Number(detail.product.document_count || 0);
    ui.loading = false;
    paint();
  }

  async function save(form) {
    const built = productUpdatePayload(form, ui.product);
    if (built.error) {
      toast(built.error, 'error');
      return;
    }
    const result = await updateProduct(ui.product.id, built.body);
    if (ui.dead) return;
    if (!result || result.error) {
      toast((result && result.error) || 'Could not save product', 'error');
      return;
    }
    toast('Product updated');
    ui.editing = false;
    await load();
    window.dispatchEvent(new CustomEvent('bonnie:products'));
  }

  async function archive() {
    const result = await archiveProduct(ui.product.id);
    if (ui.dead) return;
    if (!result || result.error) {
      toast((result && result.error) || 'Could not archive product', 'error');
      return;
    }
    toast('Product archived');
    window.dispatchEvent(new CustomEvent('bonnie:products'));
    navigate(withProduct('/app/settings/products', opts.productId));
  }

  async function restore() {
    const result = await restoreProduct(ui.product.id);
    if (ui.dead) return;
    if (!result || result.error) {
      toast((result && result.error) || 'Could not restore product', 'error');
      return;
    }
    toast('Product restored');
    window.dispatchEvent(new CustomEvent('bonnie:products'));
    await load();
  }

  ui.save = save;
  ui.archive = archive;
  ui.restore = restore;
  ui.setTab = (tab) => { ui.tab = tab; ui.editing = false; paint(); };
  ui.setEditing = (on) => { ui.editing = on; paint(); };

  load();
  return root;
}

function page(ui, ctx) {
  const product = ui.product;
  const archived = productIsArchived(product);
  const ready = readinessLabel(product);
  return el('div', { class: 'pc-page' },
    el('a', {
      class: 'pc-back',
      href: withProduct('/app/settings/products', ctx.shellProductId),
    }, '← Back to products'),
    el('header', { class: 'pc-head' },
      el('div', { class: 'pc-icon', 'aria-hidden': 'true' }, product.icon || '◈'),
      el('div', { class: 'pc-head-copy' },
        el('h1', { class: 'pc-title' }, product.name || 'Product'),
        el('p', {}, product.description || product.short_intro || product.slug || 'No description')),
      el('div', { class: 'pc-head-meta' },
        el('span', { class: `pc-chip is-${ready.tone}` }, ready.label),
        ctx.isAdmin && !archived
          ? button(ui.editing ? 'Cancel edit' : 'Edit', {
            variant: 'ghost',
            onClick: () => ui.setEditing(!ui.editing),
          })
          : null)),
    el('div', { class: 'pc-tabs', role: 'tablist' },
      tabBtn('Overview', ui.tab === 'overview', () => ui.setTab('overview')),
      tabBtn(`Connectors (${ui.integrations.length})`, ui.tab === 'connectors', () => ui.setTab('connectors')),
      tabBtn(`Knowledge (${ui.documentCount})`, ui.tab === 'knowledge', () => ui.setTab('knowledge')),
      ctx.isAdmin
        ? tabBtn('Settings', ui.tab === 'settings', () => ui.setTab('settings'))
        : null),
    ui.tab === 'connectors' ? connectorsPane(ui)
      : ui.tab === 'knowledge' ? knowledgePane(ui)
        : ui.tab === 'settings' ? settingsPane(ui)
          : overviewPane(ui, ctx));
}

function tabBtn(label, on, onClick) {
  return el('button', {
    type: 'button',
    class: on ? 'pc-tab is-on' : 'pc-tab',
    role: 'tab',
    'aria-selected': on ? 'true' : 'false',
    onClick,
  }, label);
}

function overviewPane(ui, ctx) {
  const product = ui.product;
  const stats = overviewStats({
    product,
    integrations: ui.integrations,
    documentCount: ui.documentCount,
  });
  const platforms = platformsList(product);
  if (ui.editing && ctx.isAdmin) return editForm(ui);
  return el('div', { class: 'pc-stack' },
    el('div', { class: 'pc-card' },
      el('div', { class: 'pc-grid' }, stats.map((stat) => el('div', { class: 'pc-stat' },
        el('span', { class: 'lbl' }, stat.label),
        el('span', {}, stat.value))))),
    platforms.length
      ? el('div', { class: 'pc-card' },
        el('h2', {}, 'Platforms'),
        el('div', { class: 'pc-chips' }, platforms.map((p) => el('span', { class: 'pc-chip' }, p))))
      : null,
    el('div', { class: 'pc-card' },
      el('h2', {}, 'Context for Bonnie'),
      el('p', { class: 'pc-copy' },
        product.short_intro || 'Add a short intro so answers stay on-brand for this product.'),
      el('div', { class: 'pc-links' },
        button('Open knowledge', {
          href: withProduct('/app/knowledge', product.id),
          variant: 'primary',
        }),
        button('Open connectors', {
          href: withProduct('/app/connectors', product.id),
          variant: 'ghost',
        }))));
}

function connectorsPane(ui) {
  const rows = connectorRows(ui.integrations);
  if (!rows.length) {
    return empty({
      title: 'No connectors yet',
      body: 'Connect a source so Bonnie can answer with live numbers.',
      action: button('Add a connector', {
        variant: 'primary',
        href: withProduct('/app/connectors', ui.product.id),
      }),
    });
  }
  return el('div', { class: 'pc-stack' },
    el('div', { class: 'pc-card pc-flush' },
      el('div', { class: 'pc-table lbl' },
        el('span', {}, 'Provider'),
        el('span', {}, 'Label'),
        el('span', {}, 'Type'),
        el('span', {}, 'Status')),
      rows.map((row) => {
        const health = healthLabel(row.health);
        return el('div', { class: 'pc-table-row' },
          el('strong', {}, row.name),
          el('span', {}, row.label),
          el('span', { class: 'pc-chip' }, row.type),
          el('span', { class: `pc-chip is-${health.tone}` }, health.label));
      })),
    button('Manage connectors', {
      href: withProduct('/app/connectors', ui.product.id),
      variant: 'primary',
    }));
}

function knowledgePane(ui) {
  return el('div', { class: 'pc-stack' },
    el('div', { class: 'pc-card' },
      el('h2', {}, 'Knowledge base'),
      el('p', { class: 'pc-copy' },
        ui.documentCount
          ? (`${ui.documentCount} document${ui.documentCount === 1 ? '' : 's'} for this product brain.`)
          : 'No documents yet - upload a PRD or write standing instructions.'),
      button('Open knowledge base', {
        href: withProduct('/app/knowledge', ui.product.id),
        variant: 'primary',
      })));
}

function settingsPane(ui) {
  const archived = productIsArchived(ui.product);
  let archiveBtn;
  archiveBtn = el('button', {
    type: 'button',
    class: archived ? 'pc-btn pc-btn-primary' : 'pc-btn pc-btn-danger',
    onClick: () => {
      if (archived) {
        ui.restore();
        return;
      }
      armDestructive(archiveBtn, {
        idle: 'Archive product',
        armed: 'Archive this product',
        onConfirm: () => {
          archiveBtn.disabled = true;
          ui.archive().finally(() => {
            if (!ui.dead) resetDestructive(archiveBtn, 'Archive product');
          });
        },
      });
    },
  }, archived ? 'Restore product' : 'Archive product');

  return el('div', { class: 'pc-stack' },
    el('div', { class: 'pc-card' },
      el('h2', {}, 'Project details'),
      el('p', { class: 'pc-copy' }, 'Update the name and description shown across BonnieOS.'),
      button('Edit details', {
        variant: 'ghost',
        onClick: () => ui.setEditing(true),
      })),
    el('div', { class: 'pc-card pc-danger' },
      el('h2', {}, 'Danger zone'),
      el('p', { class: 'pc-copy' }, archived
        ? 'This product is archived. Restore it to use chat, connectors, and schedules again.'
        : 'Archive hides this product from the switcher. Chats, knowledge, and connectors are kept.'),
      archiveBtn));
}

function editForm(ui) {
  const product = ui.product;
  const form = {
    name: product.name || '',
    slug: product.slug || '',
    description: product.description || '',
    category: product.category || '',
    short_intro: product.short_intro || '',
    stage: product.stage || '',
    store_category: product.store_category || '',
  };
  return el('div', { class: 'pc-card' },
    el('h2', {}, 'Edit product'),
    field('Name', el('input', {
      class: 'pc-input',
      value: form.name,
      onInput: (event) => { form.name = event.target.value; },
    })),
    field('Slug', el('input', {
      class: 'pc-input',
      value: form.slug,
      onInput: (event) => { form.slug = event.target.value; },
    })),
    field('Description', el('textarea', {
      class: 'pc-input',
      rows: '3',
      onInput: (event) => { form.description = event.target.value; },
    }, form.description)),
    field('Short intro', el('textarea', {
      class: 'pc-input',
      rows: '2',
      onInput: (event) => { form.short_intro = event.target.value; },
    }, form.short_intro)),
    field('Category', el('input', {
      class: 'pc-input',
      value: form.category,
      onInput: (event) => { form.category = event.target.value; },
    })),
    el('div', { class: 'pc-actions' },
      button('Save', { variant: 'primary', onClick: () => ui.save(form) }),
      button('Cancel', { variant: 'ghost', onClick: () => ui.setEditing(false) })));
}

function field(label, control) {
  return el('label', { class: 'pc-field' },
    el('span', { class: 'lbl' }, label),
    control);
}
